import React, { useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';
import { logger } from '../utils/logger';
import type { AppUser, Notification } from './AppContext.types';
import { User } from '../types/User'; // Changed from 'type { User }' to 'import { User }'
import { AppContext } from './AppContext.context';

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const isAuthenticated = !!user;

  // Désactiver l'initialisation automatique pour éviter les conflits avec AuthContext
  useEffect(() => {
    // AppContext ne gère plus l'authentification - délégué à AuthContext
    setIsLoading(false);
  }, []);

  const mapUserToAppUser = (currentUser: User): AppUser => {
    const [firstName = '', ...lastNameParts] = currentUser.name?.split(' ') ?? [];
    return {
      ...currentUser,
      firstName: currentUser.doctorProfile?.id ? (firstName || '') : (firstName || ''),
      lastName: lastNameParts.join(' '),
      phone: currentUser.phoneNumber ?? currentUser.phone ?? '',
      birthDate: currentUser.patientProfile?.dateOfBirth ?? currentUser.dateOfBirth ?? '',
      country: '', // À compléter si nécessaire
      city: currentUser.residence ?? '',
      gender: currentUser.patientProfile?.gender ?? '',
      specialty: currentUser.doctorProfile?.speciality ?? currentUser.speciality ?? '',
      registrationNumber: currentUser.doctorProfile?.licenseNumber ?? currentUser.licenseNumber ?? '',
      avatar: currentUser.avatar ?? '',
    } as AppUser;
  };

  const login = (userData: AppUser) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
    logger.debug('User logged out');
  };

  const refreshUser = async () => {
    try {
      const response = await authService.verifyToken();
      if (response?.user) {
        setUser(mapUserToAppUser(response.user as unknown as User));
        logger.debug('User data refreshed successfully');
      } else {
        logger.warn('No user data in token verification response');
        logout();
      }
    } catch (error: unknown) {
      logger.error('Error refreshing user:', error);
      logout();
    }
  };

  // Notifications logic (à compléter avec l'API réelle)
  const markAsRead = (id: string) => {
    setNotifications((prev) => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };
  
  const markAllAsRead = () => {
    setNotifications((prev) => prev.map(n => ({ ...n, read: true })));
  };

  // Écouter les changements de localStorage (désactivé temporairement pour debug)
  // useEffect(() => {
  //   const handleStorageChange = (e: StorageEvent) => {
  //     if (e.key === 'currentUser' || e.key === 'token') {
  //       // Logique de synchronisation
  //     }
  //   };
  //   window.addEventListener('storage', handleStorageChange);
  //   return () => window.removeEventListener('storage', handleStorageChange);
  // }, []);

  // Vérifier périodiquement la validité du token (désactivé temporairement pour debug)
  // useEffect(() => {
  //   if (!isAuthenticated) return;
  //   const checkTokenInterval = setInterval(async () => {
  //     try {
  //       const isValid = await authService.verifyToken();
  //       if (!isValid) logout();
  //     } catch (error) {
  //       logger.error('Token check error:', error);
  //     }
  //   }, 5 * 60 * 1000);
  //   return () => clearInterval(checkTokenInterval);
  // }, [isAuthenticated]);

  return (
    <AppContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoading,
      login, 
      logout, 
      refreshUser,
      notifications, 
      markAsRead, 
      markAllAsRead 
    }}>
      {children}
    </AppContext.Provider>
  );
};
